import { ChevronRight } from "lucide-react";
import { Link, Navigate } from "react-router";
import { useMe } from "@/shared/api/auth";
import { isMember, useProjects } from "@/shared/api/projects";
import { Brand } from "@/shared/ui/mark";
import { PageTitle } from "@/shared/ui/page-header";
import { SectionLabel } from "@/shared/ui/section-label";
import { LoadingScreen } from "@/core/RequireAuth";
import { projectPath } from "@/core/projects-ui";

/** Projekt-választó (`/app`): egyetlen tagságnál rögtön átirányít, egyébként listáz. */
export function ProjectsPage() {
  const { data: me } = useMe();
  const { data: projects, isPending } = useProjects();

  if (isPending || !projects) return <LoadingScreen />;

  const mine = projects.filter((p) => isMember(me, p.slug));
  const others = projects.filter((p) => !isMember(me, p.slug));

  // Egy tagság = nincs mit választani.
  if (mine.length === 1 && others.length === 0) {
    return <Navigate to={projectPath(mine[0].slug)} replace />;
  }

  return (
    <div className="mx-auto flex w-full max-w-screen-md flex-col gap-6 px-5 pb-16 pt-[18px] md:px-8">
      <div className="flex items-center justify-between">
        <Brand />
        <Link to="/app/profil" className="text-[13px] font-semibold text-gold-text">
          Profil
        </Link>
      </div>

      <PageTitle>Alkalmazások</PageTitle>

      {mine.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionLabel>Amikhez tartozol</SectionLabel>
          {mine.map((p) => (
            <ProjectRow key={p.slug} to={projectPath(p.slug)} name={p.name} lead={p.lead} />
          ))}
        </section>
      )}

      {others.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionLabel>Csatlakozhatsz</SectionLabel>
          {others.map((p) =>
            p.registration_open ? (
              <ProjectRow
                key={p.slug}
                to={`${projectPath(p.slug)}/csatlakozas`}
                name={p.name}
                lead={p.lead}
              />
            ) : (
              <div
                key={p.slug}
                className="flex flex-col gap-1 rounded-lg border border-line px-4 py-3 opacity-60"
              >
                <span className="text-[15px] font-semibold">{p.name}</span>
                <span className="text-[13px] text-muted">Jelenleg nem csatlakozható.</span>
              </div>
            ),
          )}
        </section>
      )}

      {mine.length === 0 && others.length === 0 && (
        <p className="text-[14px] text-muted">Jelenleg nincs elérhető alkalmazás.</p>
      )}
    </div>
  );
}

function ProjectRow({ to, name, lead }: { to: string; name: string; lead: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-4 rounded-lg border border-line px-4 py-3.5 transition-colors hover:bg-cream"
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{name}</span>
        {lead && <span className="text-[13px] leading-[1.4] text-muted">{lead}</span>}
      </span>
      <ChevronRight size={20} className="shrink-0 text-muted" />
    </Link>
  );
}
